import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ================ COURSE APIs ================

// Fetch all courses
export const fetchAllCourses = async (params = {}) => {
  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== "" && value !== "All" && value != null
      )
    );

    const queryString = Object.entries(filteredParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join("&");

    const url = `${BASE_URL}/courses${queryString ? `?${queryString}` : ""}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching courses:", error);
    throw error;
  }
};

// Fetch course by ID
export const fetchCourseById = async (courseId) => {
  try {
    const response = await axios.get(`${BASE_URL}/courses/${courseId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching course by ID:", error);
    throw error;
  }
};

// Create new course (Admin only)
export const createNewCourse = async (courseData, token) => {
  try {
    const response = await axios.post(`${BASE_URL}/admin/courses`, courseData, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json, text/plain, */*",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating course:", error);
    throw error;
  }
};

// Update course (Admin only)
export const updateCourse = async (courseId, courseData, token) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/admin/courses/${courseId}`,
      courseData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating course:", error);
    throw error;
  }
};

// Delete course (Admin only)
export const deleteCourse = async (courseId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/admin/courses/${courseId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting course:", error);
    throw error;
  }
};

// ================ CHAPTER APIs ================

// Fetch chapters for a course
export const fetchChaptersForCourse = async (courseId) => {
  try {
    const response = await axios.get(`${BASE_URL}/courses/${courseId}/chapters`);
    return response.data;
  } catch (error) {
    console.error("Error fetching chapters:", error);
    throw error;
  }
};

// Fetch chapter by ID
export const fetchChapterById = async (chapterId) => {
  try {
    const response = await axios.get(`${BASE_URL}/chapters/${chapterId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching chapter by ID:", error);
    throw error;
  }
};

// Create new chapter (Admin only) - nested under course
export const createNewChapter = async (chapterData, token, courseId) => {
  try {
    const response = await axios.post(`${BASE_URL}/admin/courses/${courseId}/chapters`, chapterData, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json, text/plain, */*",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating chapter:", error);
    throw error;
  }
};

// Update chapter (Admin only)
export const updateChapter = async (chapterId, chapterData, token) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/admin/chapters/${chapterId}`,
      chapterData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating chapter:", error);
    throw error;
  }
};

// Delete chapter (Admin only)
export const deleteChapter = async (chapterId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/admin/chapters/${chapterId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting chapter:", error);
    throw error;
  }
};

// ================ TOPIC APIs ================

// Fetch topics for a chapter
export const fetchTopicsForChapter = async (chapterId) => {
  try {
    const response = await axios.get(`${BASE_URL}/chapters/${chapterId}/topics`);
    return response.data;
  } catch (error) {
    console.error("Error fetching topics:", error);
    throw error;
  }
};

// Fetch topic by ID
export const fetchTopicById = async (topicId) => {
  try {
    const response = await axios.get(`${BASE_URL}/topics/${topicId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching topic by ID:", error);
    throw error;
  }
};

// Create new topic (Admin only) - nested under chapter
export const createNewTopic = async (topicData, token, chapterId) => {
  try {
    const response = await axios.post(`${BASE_URL}/admin/chapters/${chapterId}/topics`, topicData, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json, text/plain, */*",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating topic:", error);
    throw error;
  }
};

// Update topic (Admin only)
export const updateTopic = async (topicId, topicData, token) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/admin/topics/${topicId}`,
      topicData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating topic:", error);
    throw error;
  }
};

// Delete topic (Admin only)
export const deleteTopic = async (topicId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/admin/topics/${topicId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting topic:", error);
    throw error;
  }
};

// ================ REORDER APIs ================

// Reorder chapters (Admin only)
export const reorderChapters = async (orders, token) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/admin/chapters/reorder`,
      { orders },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error reordering chapters:", error);
    throw error;
  }
};

// Reorder topics within a chapter (Admin only)
export const reorderTopics = async (chapterId, orders, token) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/admin/topics/reorder`,
      { chapterId, orders },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error reordering topics:", error);
    throw error;
  }
};
